/*
 *  -----------------------------------------------------------------------
 *    <copyright file="filter.ts" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import React from 'react';
import { StudyAssessmentFieldsExtended } from '@components/admin/assessments-svt-grid/assessments-svt-grid.interface';
import DarkTooltip from '../views/components/core/dark-tool-tip/dark-tool-tip';
import { LibraryRiskFieldsExtended } from '@interfaces/library-risk.interface';
import { LibraryControlFields, StudyControlFields, StudyCrfFields } from '@queries/generated/fragments.generated';
import { studyCrfs_OpraQueries_crfs_StudyCrf } from '@queries/generated/study-crfs.generated';

const escape = (s: string) => s.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&');

const getHighlightedText = (text: string, highlight: string) => {
    // Split on highlight term and include term into parts, ignore case
    const parts = text.split(new RegExp(`(${escape(highlight)})`, 'gi'));
    return (
        <span>
            {' '}
            {parts.map((part, i) => (
                <span
                    key={`highlight-${i}`}
                    className={part.toLowerCase() === highlight.toLowerCase() ? 'highlighted-text' : ''}
                >
                    {part}
                </span>
            ))}
        </span>
    );
};

export const showWithFilter = (text: string, filter: string) =>
    text && filter && filter.length ? getHighlightedText(text, filter) : text;

export const showMaxLength = (text: string | JSX.Element, length: number, maxHeight?: string) =>
    text ? (
        React.isValidElement(text) || (text && typeof text === 'string' && text.length <= length) ? (
            text
        ) : typeof text === 'string' ? (
            // eslint-disable-next-line max-len
            // (text.length <= length ? <span title={text}>{text.substr(0, length)}...</span> : <ExpandableText title={text}>{text}</ExpandableText>) :
            <DarkTooltip
                placement="bottom"
                arrow
                leaveDelay={maxHeight ? 100 : undefined}
                classes={{ tooltip: maxHeight ? 'scrollableToolTip' : '' }}
                title={maxHeight ? <div style={{ maxHeight, overflowY: 'auto' }}>{text}</div> : text}
            >
                <span>{text.substr(0, length)}...</span>
            </DarkTooltip>
        ) : (
            text
        )
    ) : (
        ''
    );

export const getAllControls = (
    libraryControlData: any,
    controls: any,
    currentRisk: any,
    libraryRisk?: any,
    isSystem?: boolean,
): [any] => {
    let allControls = [];
    if (libraryRisk !== undefined && libraryRisk !== null && libraryRisk?.libraryControlIds?.length) {
        allControls =
            libraryControlData && controls
                ? libraryControlData
                      // Don't include any that have already been added from the library
                      .filter(
                          (control: LibraryControlFields) =>
                              !controls.find((r: any) => {
                                  const id = r.libraryControlId || r.libraryReferenceId;
                                  return id === control.id;
                              }) && control.active,
                      )
                      .map(
                          // All risk library items get a book icon
                          (control: StudyControlFields) => ({
                              ...control,
                              isLinkIcon: libraryRisk?.libraryControlIds.indexOf(control.id) >= 0 ?? false,
                              icon: !isSystem ? 'book-open' : null,
                              library: true,
                          }),
                      )
                : [];
    } else {
        allControls =
            libraryControlData && controls
                ? libraryControlData
                      // Don't include any that have already been added from the library
                      .filter(
                          (control: LibraryControlFields) =>
                              !controls.find((r: any) => {
                                  const id = r.libraryControlId || r.libraryReferenceId;
                                  return id === control.id;
                              }) && control.active,
                      )
                      .map(
                          // All risk library items get a book icon
                          (control: StudyControlFields) => ({
                              ...control,
                              icon: !isSystem ? 'book-open' : null,
                              isLinkIcon: false,
                              library: true,
                          }),
                      )
                : [];
    }
    if (controls?.length) {
        allControls = allControls.concat(
            controls
                .filter((control: any) => (control.active !== undefined ? control.active : true))
                // handling case when study controls don't have active field
                .map((control: any) => ({
                    ...control,
                    library: false,
                    isLinkIcon:
                        (libraryRisk &&
                            (control.associatedSystemRisks?.indexOf(libraryRisk.id) >= 0 ||
                                libraryRisk?.libraryControlIds.indexOf(control.libraryControlId) >= 0)) ??
                        false,
                })),
        );
    }
    const controlsFromRisk = currentRisk?.libraryControls || currentRisk?.controls || [];
    if (controls) {
        if (controlsFromRisk.length)
            allControls = allControls.filter(
                (control: any) => controlsFromRisk.findIndex((item: any) => item.id === control.id) < 0,
            );
        else if (currentRisk?.libraryControlIds)
            allControls = allControls.filter((control: any) => currentRisk?.libraryControlIds.indexOf(control.id) < 0);
        else if (currentRisk?.controlIds)
            allControls = allControls.filter((control: any) => currentRisk?.controlIds.indexOf(control.id) < 0);
    }
    return allControls;
};

export const getAvailableControls = (
    libraryControlData: any,
    controls: any,
    currentRisk: any,
    includeLibrary: boolean,
    filter: string,
    libraryRisk?: any,
    isSystem?: boolean,
): { allControlsLength: number; filteredControls: any[] } => {
    const allAvailableControls = getAllControls(libraryControlData, controls, currentRisk, libraryRisk, isSystem)
        .filter((control: any) => (includeLibrary || includeLibrary === control.library) && !control.inactiveDate)
        .map((control: LibraryControlFields) => ({
            ...control,
            isLinkIcon:
                (libraryRisk &&
                    (control.associatedSystemRisks?.indexOf(libraryRisk.id) >= 0 ||
                        libraryRisk?.libraryControlIds.indexOf(control.id) >= 0)) ??
                false,
        }));
    const filteredControls = allAvailableControls.filter(
        (control: any) => control.description.toUpperCase().indexOf(filter) >= 0,
    );
    return {
        allControlsLength: allAvailableControls.length,
        filteredControls,
    };
};

export const getAllRisksControl = (
    libraryRisks: LibraryRiskFieldsExtended[],
    risksData: any = [],
    currentControl: any,
    isSystem?: boolean,
) => {
    let risks: LibraryRiskFieldsExtended[] = libraryRisks
        ? libraryRisks.map(
              // All risk library items get a book icon
              (risk: LibraryRiskFieldsExtended) => ({
                  ...risk,
                  icon: !isSystem ? 'book-open' : null,
                  library: true,
              }),
          )
        : [];

    risks = risks
        .concat(
            risksData.map((risk: any) => ({
                ...risk,
                library: false,
                isLinkIcon: currentControl?.associatedSystemRisks.indexOf(risk.systemReferenceId) >= 0 ?? false,
            })),
        )
        .filter((risk: LibraryRiskFieldsExtended) =>
            currentControl ? risk.libraryControlIds.indexOf(currentControl.id) < 0 && risk.active : risk.active,
        );

    return risks;
};

export const getAvailableRisks = (
    libraryRisks: LibraryRiskFieldsExtended[],
    risks: any,
    currentControl: any,
    filter: string,
    includeLibrary: boolean,
    isSystem?: boolean,
) =>
    getAllRisksControl(libraryRisks, risks, currentControl, isSystem).filter((risk: any) => {
        if (includeLibrary || includeLibrary === risk.library) {
            return !filter.length ? true : risk.statement.toUpperCase().indexOf(filter) >= 0;
        }
        return false;
    });

export interface IByUser {
    firstName: string | null;
    lastName: string | null;
    email: string | null;
}

export const formUpdatedCreatedField = (data?: IByUser | string) => {
    if (typeof data === 'string') return data || '';

    return data ? `${data.firstName || ''} ${data.lastName || ''}` : '';
};

export interface RestructuredUpdatedCreatedBy {
    _createdByString?: string;
    _updatedByString?: string;
}

// mutate existing array with adding two new fields
// Obsolete, if further use is needed use the same function in update-users-array.ts
export const restructuredUpdatedCreatedByObsolete = (items: any[]): any[] => {
    if (items) {
        /* eslint-disable @typescript-eslint/no-explicit-any */
        return items.map((item: any) => ({
            ...item,
            _createdByString: formUpdatedCreatedField(item.createdBy ?? item.createdByName),
            _updatedByString: formUpdatedCreatedField(item.updatedBy ?? item.updatedByName),
        }));
    }

    return [];
};

export const handleTimeInDates = (array: any[], dateFields: string[]) =>
    array.map((item: any) => {
        dateFields.forEach((el: any) => {
            item[el] = item[el] ? new Date(item[el].split('T')[0]).toISOString() : item[el];
        });
        return item;
    });

export const reorderArray = (array1: any[], array2: any[]) =>
    array2.slice().sort((a: any, b: any) => array1.indexOf(a.id) - array1.indexOf(b.id));

export const addCRFNameToAssessment = (
    assessments: StudyAssessmentFieldsExtended[],
    crfs: StudyCrfFields[],
    assessmentAssociation: boolean | null | undefined,
) =>
    assessments.map((item: any) => {
        const crf = crfs?.find(
            (crfItem: StudyCrfFields | (studyCrfs_OpraQueries_crfs_StudyCrf | null)) => crfItem?.id === item.crfId,
        );

        return {
            ...item,
            _crfName: assessmentAssociation ? crf?.crfName : '',
        };
    });
